/**
 * /dashboard/expenses — Expense Tracking hub, "My expenses" view
 * (SOP §1.2; Phase 3d — 2026-06-01).
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/expenses/page.tsx flattened
 * with the old `layout.tsx` into one route that uses ExpensesModuleShell.
 * Legacy `?tab=` redirect helper is dropped — TanStack typed routes
 * are the only entry point.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ReceiptIndianRupee } from "lucide-react";
import { ExpensesModuleShell } from "@/components/shared/expenses-module-shell";
import { ExpenseFeatureGate } from "@/components/expenses/expense-feature-gate";
import { ExpensesTab } from "@/components/expenses/expenses-tab";
import { useExpenseRoles } from "@/components/expenses/use-expense-roles";
import { Button } from "@/components/ui/button";
import { expenseKeys } from "@/hooks/use-expenses";
import { expenseApi } from "@/lib/api";
import { expensesSearchSchema } from "@/lib/my-expense-claims";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/expenses/")({
  // ?status= pre-filters My claims — the Me-home "awaiting approval" row
  // links here with ?status=submitted so the list shows exactly what it
  // counted (persona round 2026-10, lib/my-expense-claims).
  validateSearch: expensesSearchSchema,
  // A9 hover-prefetch. Primary = the caller's OWN ("mine") expense list —
  // the self-view default of ExpenseList for every role (2026-08 UX
  // remediation: the Me surface defaults to "My claims"; approvers reach
  // the exclude_self approver queue via the toggle / the /approvals
  // inbox, whose loader warms that bucket). Category catalogue is
  // secondary → fire-and-forget.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery({
      queryKey: expenseKeys.categories(),
      queryFn: ({ signal }) => expenseApi.listExpenseCategories(signal),
    });
    return context.queryClient
      .ensureQueryData({
        queryKey: expenseKeys.list(undefined, undefined, undefined, "mine"),
        queryFn: ({ signal }) =>
          expenseApi.listExpenses({ mine: true }, signal),
      })
      .catch(() => undefined);
  },
  component: ExpensesHubRoute,
});

function ExpensesHubRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { isApprover } = useExpenseRoles();
  const { status } = Route.useSearch();
  return (
    <ExpensesModuleShell
      title="Claims"
      description={
        isApprover
          ? "Review and act on expense claims, and manage your own."
          : "File expense claims and track reimbursement."
      }
      actions={
        <Button asChild className="gap-1.5">
          <Link to="/app/$tenantSlug/expenses/new" params={{ tenantSlug }}>
            <ReceiptIndianRupee className="h-4 w-4" />
            New Expense
          </Link>
        </Button>
      }
    >
      <ExpenseFeatureGate>
        <ExpensesTab initialStatus={status} />
      </ExpenseFeatureGate>
    </ExpensesModuleShell>
  );
}
